<template>
  <div class="ack-document-create">
    <h3 class="ack-document-create__title">{{ t('hubstr.ack_documents.create.title') }}</h3>

    <el-alert
      class="ack-document-create__alert"
      type="info"
      :closable="false"
      :title="t('hubstr.ack_documents.create.immutable_hint')"
    />

    <el-form label-position="top" @submit.prevent>
      <el-form-item
        :label="t('hubstr.ack_documents.create.title_label')"
        :error="state.fieldErrors.title"
        required
      >
        <el-input
          v-model="state.title"
          maxlength="255"
          show-word-limit
          :placeholder="t('hubstr.ack_documents.create.title_placeholder')"
          :disabled="state.loading"
        />
      </el-form-item>

      <el-form-item
        :label="t('hubstr.ack_documents.create.file_label')"
        :error="state.fieldErrors.fileId"
        required
      >
        <div class="ack-document-create__file">
          <magner-form-dropzone
            v-model="state.file"
            :field="dropZoneField"
            @update:modelValue="fileSelected"
          />
          <p class="ack-document-create__hint">{{ t('hubstr.ack_documents.create.file_hint') }}</p>
        </div>
      </el-form-item>

      <el-form-item :label="t('hubstr.ack_documents.create.audience_label')" required>
        <el-radio-group v-model="state.audience" :disabled="state.loading">
          <el-radio label="all">{{ t('hubstr.ack_documents.audience.all') }}</el-radio>
          <el-radio label="labels">{{ t('hubstr.ack_documents.audience.labels') }}</el-radio>
        </el-radio-group>
      </el-form-item>

      <el-form-item
        v-if="state.audience === 'labels'"
        :label="t('hubstr.ack_documents.create.labels_label')"
        :error="state.fieldErrors.criteria"
        required
      >
        <el-select
          v-model="state.tagIds"
          multiple
          filterable
          remote
          collapse-tags
          collapse-tags-tooltip
          class="ack-document-create__select"
          :remote-method="searchTags"
          :loading="state.tagsLoading"
          :placeholder="t('hubstr.ack_documents.create.labels_placeholder')"
          :disabled="state.loading"
          @visible-change="(visible: boolean) => visible && !state.tags.length && searchTags('')"
        >
          <el-option
            v-for="tag in state.tags"
            :key="tag.id"
            :label="tag.name"
            :value="tag.id"
          />
        </el-select>
        <p class="ack-document-create__hint">{{ t('hubstr.ack_documents.create.labels_hint') }}</p>
      </el-form-item>
    </el-form>

    <p v-if="state.errorText" class="ack-document-create__error">{{ state.errorText }}</p>

    <div class="ack-document-create__actions">
      <el-button type="primary" :loading="state.loading" @click="submit">
        {{ t('hubstr.ack_documents.create.submit') }}
      </el-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive } from 'vue';
import { MagnerFormDropzone, translate, useTranslate } from 'magner';
import { ElMessage } from 'magner/element-plus';
import { ackDocumentCreate, ackDocumentUploadPdf, logAckInfo } from 'features/ack-documents/requests';
import { ACK_DOCUMENT_MAX_FILE_SIZE, AckDocumentTag } from 'features/ack-documents/types';
import { searchPositionTags } from 'features/settings/notification/requests/hubstr';

// Magner-модалка слушает у содержимого только success; закрытие без сохранения — крестиком
const emit = defineEmits(['success']);

const { customT } = useTranslate();
const t = (code: string) => customT(translate(code));

const state = reactive<{
  title: string,
  file: File[] | null,
  /** id уже загруженного PDF: при ошибке create не грузим файл повторно, пока его не заменили */
  uploadedFileId: string | null,
  audience: 'all' | 'labels',
  tagIds: string[],
  tags: AckDocumentTag[],
  tagsLoading: boolean,
  loading: boolean,
  errorText: string,
  fieldErrors: Record<string, string>,
}>({
  title: '',
  file: null,
  uploadedFileId: null,
  audience: 'all',
  tagIds: [],
  tags: [],
  tagsLoading: false,
  loading: false,
  errorText: '',
  fieldErrors: {},
});

const dropZoneField = {
  type: 'dropzone',
  name: 'file',
  props: {
    valueKey: 'id',
    srcKey: 'file',
    removable: true,
    // formats/maxSize не задаём: dropzone молча отбрасывает такой файл (ошибку шлёт только в textErrors),
    // тип и размер проверяет fileSelected и показывает ошибку под полем
    inputAtts: {
      accept: '.pdf,application/pdf',
    },
  },
};

const isPdf = (file: File) => file.type === 'application/pdf' || /\.pdf$/i.test(file.name);

/** Новый файл — сбрасываем id прежней загрузки и проверяем тип и размер на клиенте */
const fileSelected = () => {
  state.uploadedFileId = null;
  state.fieldErrors.fileId = '';

  const file = state.file?.[0];
  if (!file) {
    logAckInfo('create', 'file removed');
    return;
  }

  logAckInfo('create', 'file selected', { name: file.name, size: file.size, type: file.type });

  if (!isPdf(file)) {
    console.warn('[AckDocuments.create] rejected file type', { type: file.type });
    state.fieldErrors.fileId = t('hubstr.ack_documents.create.error_file_type');
  } else if (file.size > ACK_DOCUMENT_MAX_FILE_SIZE) {
    console.warn('[AckDocuments.create] rejected file size', { size: file.size });
    state.fieldErrors.fileId = t('hubstr.ack_documents.create.error_file_size');
  }
};

const searchTags = async (query: string) => {
  state.tagsLoading = true;
  try {
    const res = await searchPositionTags({ search: query } as any);
    const rows = ((res as any)?.data?.rows ?? []) as AckDocumentTag[];
    // Выбранные лейблы оставляем в опциях, иначе select покажет вместо названий id
    const selected = state.tags.filter((tag) => state.tagIds.includes(tag.id));
    state.tags = [...selected, ...rows.filter((tag) => !state.tagIds.includes(tag.id))];
    logAckInfo('create', 'labels loaded', { count: rows.length });
  } catch (e) {
    console.error('[AckDocuments.create] labels request failed', e);
  } finally {
    state.tagsLoading = false;
  }
};

const validate = (): boolean => {
  const errors: Record<string, string> = {};
  const file = state.file?.[0];

  if (!state.title.trim()) {
    errors.title = t('hubstr.ack_documents.create.error_title_required');
  }
  if (!file) {
    errors.fileId = t('hubstr.ack_documents.create.error_file_required');
  } else if (!isPdf(file)) {
    errors.fileId = t('hubstr.ack_documents.create.error_file_type');
  } else if (file.size > ACK_DOCUMENT_MAX_FILE_SIZE) {
    errors.fileId = t('hubstr.ack_documents.create.error_file_size');
  }
  if (state.audience === 'labels' && !state.tagIds.length) {
    errors.criteria = t('hubstr.ack_documents.create.error_labels_required');
  }

  state.fieldErrors = errors;
  if (Object.keys(errors).length) {
    console.warn('[AckDocuments.create] client validation failed', { fields: Object.keys(errors) });
    return false;
  }
  return true;
};

const submit = async () => {
  state.errorText = '';
  if (!validate()) {
    return;
  }

  state.loading = true;
  try {
    if (!state.uploadedFileId) {
      const upload = await ackDocumentUploadPdf((state.file as File[])[0]);
      if (upload.error || !upload.data) {
        state.fieldErrors = { fileId: upload.error || t('hubstr.ack_documents.create.error_upload') };
        return;
      }
      state.uploadedFileId = upload.data;
    }

    const res = await ackDocumentCreate({
      title: state.title.trim(),
      fileId: state.uploadedFileId,
      criteria: state.audience === 'labels' ? [{ type: 'position_tags', tagIds: state.tagIds }] : [],
    });

    if (res.error || !res.data) {
      state.fieldErrors = res.fields ?? {};
      state.errorText = res.error ?? t('hubstr.ack_documents.errors.common');
      return;
    }

    logAckInfo('create', 'document created', { id: res.data.id });
    ElMessage.success(t('hubstr.ack_documents.create.success'));
    emit('success', res.data);
  } catch (e) {
    console.error('[AckDocuments.create] unexpected error', e);
    state.errorText = t('hubstr.ack_documents.errors.common');
  } finally {
    state.loading = false;
  }
};
</script>
